import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

const languages: Language[] = [
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
];

interface LanguageSwitcherProps {
  className?: string;
  compact?: boolean;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ className = '', compact = false }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = async (languageCode: string) => {
    try {
      await i18n.changeLanguage(languageCode);
      
      // Set RTL for Arabic
      if (languageCode === 'ar') {
        document.documentElement.dir = 'rtl';
        document.documentElement.lang = 'ar';
      } else {
        document.documentElement.dir = 'ltr';
        document.documentElement.lang = languageCode;
      }

      // Update user preference via API if authenticated
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/locale`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ preferred_locale: languageCode }),
          });
          
          if (response.ok) {
            console.log('Language preference saved to user profile');
          }
        }
      } catch (error) {
        console.warn('Failed to save language preference to server:', error);
      }

      setIsOpen(false);
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  if (compact) {
    return (
      <div className={`relative ${className}`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 px-2 py-1 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          data-testid="language-switcher-compact"
        >
          <Globe className="w-4 h-4" />
          <span className="text-lg">{currentLanguage.flag}</span>
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute top-full right-0 mt-2 w-40 bg-[var(--surface)] border border-[var(--border-light)] rounded-lg shadow-lg z-20">
              {languages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => handleLanguageChange(language.code)}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-[var(--gray-50)] transition-colors first:rounded-t-lg last:rounded-b-lg flex items-center space-x-2 ${
                    language.code === i18n.language ? 'bg-[var(--color-primary-50)] text-[var(--color-primary)]' : 'text-[var(--text-primary)]'
                  }`}
                  data-testid={`language-option-${language.code}`}
                >
                  <span className="text-base">{language.flag}</span>
                  <span>{language.nativeName}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 bg-[var(--surface)] border border-[var(--border-light)] rounded-lg hover:bg-[var(--gray-50)] transition-colors"
        data-testid="language-switcher"
      >
        <Globe className="w-4 h-4 text-[var(--color-primary)]" />
        <span className="text-lg">{currentLanguage.flag}</span>
        <span className="font-medium text-[var(--text-primary)]">{currentLanguage.nativeName}</span>
        <ChevronDown className="w-4 h-4 text-[var(--text-secondary)]" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full left-0 mt-2 w-full min-w-48 bg-[var(--surface)] border border-[var(--border-light)] rounded-lg shadow-lg z-20">
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-[var(--gray-50)] transition-colors first:rounded-t-lg last:rounded-b-lg flex items-center space-x-3 ${
                  language.code === i18n.language ? 'bg-[var(--color-primary-50)] text-[var(--color-primary)]' : 'text-[var(--text-primary)]'
                }`}
                data-testid={`language-option-${language.code}`}
              >
                <span className="text-base">{language.flag}</span>
                <div>
                  <div className="font-medium">{language.nativeName}</div>
                  <div className="text-xs text-[var(--text-secondary)]">{language.name}</div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSwitcher;